import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Tag, Form, Input, Switch, InputNumber, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/axios';

interface AdminOleMenuDto {
    id: string;
    name: string;
    url: string;
    orderIndex: number;
    openInNewWindow: boolean;
    availableLanguages: string[];
    subMenus: AdminOleMenuDto[];
    children?: AdminOleMenuDto[];
}

export const OleMenus: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingMenuId, setEditingMenuId] = useState<string | null>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const appLanguages = ['de', 'en', 'tr'];

    const { data: menus, isLoading } = useQuery({
        queryKey: ['adminOleMenus'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/ole/menus');
            
            const mapChildren = (items: AdminOleMenuDto[]): AdminOleMenuDto[] => {
                return items.map(item => ({
                    ...item,
                    children: item.subMenus && item.subMenus.length > 0 ? mapChildren(item.subMenus) : undefined
                }));
            };
            return mapChildren(response.data.data || []);
        }
    });

    const { refetch: fetchMenuDetails } = useQuery({
        queryKey: ['adminOleMenuDetail', editingMenuId],
        queryFn: async () => {
            if (!editingMenuId) return null;
            const response = await apiClient.get(`/admin/ole/menus/${editingMenuId}`);
            return response.data.data;
        },
        enabled: false
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/ole/menus/${id}`);
        },
        onSuccess: () => {
            message.success('Menü başarıyla silindi.');
            queryClient.invalidateQueries({ queryKey: ['adminOleMenus'] });
        },
        onError: () => {
            message.error('Menü silinirken hata oluştu.');
        }
    });

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const title = values[`translations_${lang}_name`];
                if (!title) return null;
                return { languageCode: lang, name: title };
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error('En az bir dilde isim girmelisiniz.');
            }

            const payload = {
                id: editingMenuId || undefined,
                parentId: values.parentId || null,
                url: values.url,
                orderIndex: values.orderIndex,
                isActive: values.isActive,
                openInNewWindow: values.openInNewWindow,
                translations
            };

            if (editingMenuId) {
                await apiClient.put(`/admin/ole/menus/${editingMenuId}`, payload);
            } else {
                await apiClient.post('/admin/ole/menus', payload);
            }
        },
        onSuccess: () => {
            message.success('Menü başarıyla kaydedildi.');
            setIsModalVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['adminOleMenus'] });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Menü kaydedilirken hata oluştu.');
        }
    });

    const handleEdit = async (id: string) => {
        setEditingMenuId(id);
        const { data: menuDetail } = await fetchMenuDetails();
        if (menuDetail) {
            const formValues: any = {
                url: menuDetail.url,
                orderIndex: menuDetail.orderIndex,
                isActive: menuDetail.isActive,
                openInNewWindow: menuDetail.openInNewWindow,
                parentId: menuDetail.parentId
            };
            appLanguages.forEach(lang => {
                const trans = menuDetail.translations?.find((t: any) => t.languageCode === lang);
                if (trans) {
                    formValues[`translations_${lang}_name`] = trans.name;
                }
            });
            form.setFieldsValue(formValues);
            setIsModalVisible(true);
        }
    };

    const handleCreate = (parentId?: string) => {
        setEditingMenuId(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            orderIndex: 0,
            openInNewWindow: false,
            parentId: parentId || null
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Bu menüyü silmek istediğinize emin misiniz?',
            content: 'Alt menüler de etkilenecektir. Bu işlem geri alınamaz.',
            okText: 'Evet, Sil',
            okType: 'danger',
            cancelText: 'İptal',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { title: 'Menü Adı', dataIndex: 'name', key: 'name' },
        { title: 'URL', dataIndex: 'url', key: 'url' },
        { title: 'Sıra', dataIndex: 'orderIndex', key: 'orderIndex' },
        {
            title: 'Çeviriler',
            dataIndex: 'availableLanguages',
            key: 'availableLanguages',
            render: (langs: string[]) => (
                <Space size={[0, 8]} wrap>
                    {langs?.map(l => <Tag key={l}>{l.toUpperCase()}</Tag>)}
                </Space>
            )
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_: any, record: AdminOleMenuDto) => (
                <Space size="middle">
                    <Button size="small" icon={<PlusOutlined />} onClick={() => handleCreate(record.id)}>Alt Menü Ekle</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        children: (
            <Form.Item name={`translations_${lang}_name`} label="Menü Adı">
                <Input placeholder={`${lang.toUpperCase()} Adı`} />
            </Form.Item>
        )
    }));

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>OLE Menü Yönetimi</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate()}>
                    Ana Menü Ekle
                </Button>
            </div>

            <Table
                dataSource={menus}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={false}
            />

            <Modal
                title={editingMenuId ? 'Menüyü Düzenle' : 'Yeni Menü'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={saveMutation.isPending}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
                    <Form.Item name="parentId" hidden><Input /></Form.Item>

                    <Tabs type="card" items={tabItems} style={{ marginBottom: 24 }} />

                    <Form.Item name="url" label="URL (ör: /hakkimizda, veya /#iletisim)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Space size="large" style={{ display: 'flex' }}>
                        <Form.Item name="orderIndex" label="Sıra">
                            <InputNumber min={0} />
                        </Form.Item>
                        <Form.Item name="isActive" label="Aktif" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item name="openInNewWindow" label="Yeni Sekmede Aç" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};
